import React, { useRef, useEffect, useState } from 'react'
import moment from 'moment'
import v4 from 'uuid/v4'
import { View } from 'react-native'
import { useSelector, useDispatch } from 'react-redux'

import DismissibleItem from '../../UIComponents/DismissibleItem'
import store from '../../../redux/store'
import { BACKLOG_DATE_STRING } from '../Utils/TasksHelper'
import { isInputsFocused } from '../../../utils/HelperFunctions'
import AddTask from '../AddTask'
import Backend from '../../../utils/BackendBridge'
import { DATE_TASK_INDEX, TODAY_DATE } from '../../../utils/backends/openTasks'
import EditTask from '../TaskItem/EditTask'
import { setAddTaskSectionToOpenData } from '../../../redux/actions'
import { taskEditorLayout } from '../TaskItem/TaskEditorLayout'
import useTaskEditorLock from '../../../hooks/useTaskEditorLock'
import InlineAddTaskFileDrop from '../InlineAddTaskFileDrop'

function AddTaskEditor(props) {
    const taskEditorLock = useTaskEditorLock()

    useEffect(() => {
        taskEditorLock.acquire()
        return taskEditorLock.release
    }, [taskEditorLock])

    return <EditTask {...props} />
}

export default function NewTaskSection({
    projectId,
    hideItemFutureTasks,
    originalParentGoal,
    hideParentGoalButton,
    instanceKey,
    dateIndex,
    expandTasksList,
    isLocked,
    suspendShortcut = false,
}) {
    const dispatch = useDispatch()
    const dateFormated = useSelector(
        state => state.filteredOpenTasksStore[instanceKey]?.[dateIndex]?.[DATE_TASK_INDEX] || TODAY_DATE
    )
    const addTaskSectionToOpenData = useSelector(state => state.addTaskSectionToOpenData)
    const newItemRef = useRef(null)
    const [activeGoal, setActiveGoal] = useState(originalParentGoal || null)
    const activeGoalIdRef = useRef(originalParentGoal?.id || null)

    const dateIsToday = dateFormated === TODAY_DATE
    const date = dateIsToday ? moment() : moment(dateFormated, 'YYYYMMDD')

    const onKeyDown = e => {
        if (store.getState().blockShortcuts || isLocked || suspendShortcut) {
            return
        }
        const { lastAddNewTaskDate } = store.getState()
        const { projectId: lastPId, date: lastAddDate } = lastAddNewTaskDate
            ? lastAddNewTaskDate
            : { projectId: null, date: null }

        const shouldOpen =
            (lastAddDate == null && projectId === lastPId && date.isSame(moment(), 'day')) ||
            (lastAddDate != null && projectId === lastPId && date.isSame(moment(lastAddDate), 'day'))

        const dismissItems = document.querySelectorAll('[aria-label="dismissible-edit-item"]')
        if (e.key === '+' && dismissItems.length === 0 && !isInputsFocused() && shouldOpen) {
            e.preventDefault()
            e.stopPropagation()
            newItemRef.current.toggleModal()
        }
    }

    const updateActiveGoal = goal => {
        activeGoalIdRef.current = goal ? goal.id : null
        setActiveGoal(goal)
    }

    useEffect(() => {
        if (originalParentGoal && (!activeGoal || originalParentGoal.id === activeGoal.id)) {
            updateActiveGoal(originalParentGoal)
        }
    }, [originalParentGoal])

    useEffect(() => {
        if (activeGoalIdRef.current && (!originalParentGoal || originalParentGoal.id !== activeGoalIdRef.current)) {
            const watcherKey = v4()
            const oldActiveGoalId = activeGoalIdRef.current
            Backend.watchGoal(projectId, activeGoalIdRef.current, watcherKey, goal => {
                if (oldActiveGoalId === activeGoalIdRef.current) {
                    updateActiveGoal(goal)
                }
            })
            return () => {
                Backend.unwatch(projectId, watcherKey)
            }
        }
    }, [activeGoalIdRef.current])

    useEffect(() => {
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('keydown', onKeyDown)
        }
    })

    const processAutoOpenWhenSelectGoal = addTaskSectionToOpenData => {
        const { projectId: projectIdToOpen, goalId, dateFormated: dateFormatedToOpen } = addTaskSectionToOpenData

        if (projectId === projectIdToOpen && dateFormatedToOpen === dateFormated) {
            const shouldOpenThisSection = goalId
                ? originalParentGoal && goalId === originalParentGoal.id
                : !originalParentGoal

            if (!shouldOpenThisSection) return

            dispatch(setAddTaskSectionToOpenData(null))
            const inputIsOpen = newItemRef.current.modalIsVisible()
            if (!inputIsOpen) newItemRef.current.openModal(true)
        }
    }

    useEffect(() => {
        if (addTaskSectionToOpenData) processAutoOpenWhenSelectGoal(addTaskSectionToOpenData)
    }, [addTaskSectionToOpenData])

    // AT-2875: dropping files on this line opens the add-task popup with them attached.
    const fileDropEnabled = !hideItemFutureTasks && !isLocked

    return (
        <View style={taskEditorLayout.addTaskSection}>
            <InlineAddTaskFileDrop
                projectId={projectId}
                enabled={fileDropEnabled}
                inlineEditorRef={newItemRef}
                activeGoal={activeGoal}
                defaultDate={date.valueOf()}
                inBacklog={dateFormated === BACKLOG_DATE_STRING}
                expandTasksList={expandTasksList}
            >
                <DismissibleItem
                    ref={newItemRef}
                    defaultComponent={
                        !hideItemFutureTasks && (
                            <AddTask
                                projectId={projectId}
                                tags={[]}
                                toggleModal={() => {
                                    newItemRef?.current?.toggleModal()
                                }}
                                newItem={true}
                                activeGoal={activeGoal}
                                hideParentGoalButton={hideParentGoalButton}
                                isLocked={isLocked}
                                dateFormated={dateFormated}
                            />
                        )
                    }
                    modalComponent={
                        <AddTaskEditor
                            adding={true}
                            projectId={projectId}
                            onCancelAction={forceAction => {
                                newItemRef?.current?.toggleModal(forceAction)
                            }}
                            defaultDate={date.valueOf()}
                            inBacklog={dateFormated === BACKLOG_DATE_STRING}
                            originalParentGoal={originalParentGoal}
                            activeGoal={activeGoal}
                            expandTasksList={expandTasksList}
                            dateFormated={dateFormated}
                        />
                    }
                />
            </InlineAddTaskFileDrop>
        </View>
    )
}
